import { useCallback, useEffect, useRef, useState } from 'react';
import { apiService } from '@/services/api';
import { quoteExpired } from '@/lib/followupQuote';

// §branded-refill: owns GET /followup/quote for a follow-up session.
//
// Fetched once the dashboard-launched patient is verified (we then hold the
// prior masterId); re-fetched when the patient asks for a different dose, when
// the backend says the quote went stale on the payment call, and when a quote
// expires before checkout. Any failure → quote null → today's flow. We never
// retry in a loop: the next page load tries again.
//
// The INITIAL quote is raced against a short timeout: the form holds its first
// slide until the quote answers (the quote can change which questionnaire
// runs), and a patient must never wait on a slow backend for a cosmetic win.
// On timeout the quote is treated as unavailable (today's flow) and a late
// answer is ignored, so the questionnaire can't switch under them mid-flow.
export const INITIAL_QUOTE_TIMEOUT_MS = 8000;

// status: 'idle' | 'loading' | 'ready' | 'unavailable'
export default function useFollowupQuote({ enabled, masterId, rxId, rxRowId, medId, fulfillmentRoute }) {
  const [quote, setQuote] = useState(null);
  const [status, setStatus] = useState('idle');
  const [requoting, setRequoting] = useState(false);
  // Last extra params sent, so a dose change that resolves to the same request
  // (e.g. keep → keep) doesn't refetch.
  const lastExtraRef = useRef({ requestedMedId: null, fulfillmentRoute: fulfillmentRoute || null });
  const seq = useRef(0);
  // The Rx's own medId, from the first (un-requested) quote. A dose re-quote
  // returns quote.medId = the requested strength; this keeps "current dose"
  // pointing at the prescription actually being refilled.
  const rxMedIdRef = useRef(null);

  const fetchQuote = useCallback(
    async (extra = {}) => {
      // masterId alone is enough (BE #337/#339 quotes the visit's latest Rx);
      // rxId / rxRowId / medId narrow it when the launch carried one.
      if (!masterId) return null;
      const mine = ++seq.current;
      const params = { masterId };
      if (rxId) params.rxId = rxId;
      else if (rxRowId) params.rxRowId = rxRowId;
      else if (medId) params.medId = medId;
      const route = extra.fulfillmentRoute !== undefined ? extra.fulfillmentRoute : lastExtraRef.current.fulfillmentRoute;
      const req = extra.requestedMedId !== undefined ? extra.requestedMedId : lastExtraRef.current.requestedMedId;
      if (route) params.fulfillmentRoute = route;
      if (req) params.requestedMedId = req;
      lastExtraRef.current = { requestedMedId: req || null, fulfillmentRoute: route || null };
      try {
        const q = await apiService.getFollowupQuote(params);
        if (mine !== seq.current) return null; // a newer request superseded this one
        if (q && q.quoteId) {
          if (!req || !rxMedIdRef.current) rxMedIdRef.current = q.rxMedId || q.medId;
          const stamped = q.rxMedId ? q : { ...q, rxMedId: rxMedIdRef.current };
          setQuote(stamped);
          setStatus('ready');
          return stamped;
        }
        setQuote(null);
        setStatus('unavailable');
        return null;
      } catch (e) {
        if (mine !== seq.current) return null;
        // 404 = flag off / no Rx row (today's flow). Anything else is treated
        // the same way — never invent a price.
        const st = e && e.response && e.response.status;
        if (st !== 404) console.warn('[followup-quote] unavailable', st, e && e.message);
        setQuote(null);
        setStatus('unavailable');
        return null;
      }
    },
    [masterId, rxId, rxRowId, medId],
  );

  // Initial fetch.
  useEffect(() => {
    if (!enabled || status !== 'idle') return;
    if (!masterId) return;
    setStatus('loading');
    const started = seq.current + 1; // the seq fetchQuote will take
    const timer = setTimeout(() => {
      if (seq.current !== started) return; // superseded or already answered
      seq.current += 1; // a late answer is ignored
      setQuote(null);
      setStatus('unavailable');
      console.warn('[followup-quote] timed out; running today\'s flow');
    }, INITIAL_QUOTE_TIMEOUT_MS);
    fetchQuote().finally(() => clearTimeout(timer));
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, status, masterId]);

  // Re-quote for a dose request (requestedMedId null = back to "keep").
  const requote = useCallback(
    async (extra = {}) => {
      if (status === 'idle' || status === 'loading') return null;
      const nextReq = extra.requestedMedId === undefined ? lastExtraRef.current.requestedMedId : (extra.requestedMedId || null);
      const nextRoute = extra.fulfillmentRoute === undefined ? lastExtraRef.current.fulfillmentRoute : (extra.fulfillmentRoute || null);
      const same =
        nextReq === lastExtraRef.current.requestedMedId && nextRoute === lastExtraRef.current.fulfillmentRoute;
      if (same && !extra.force) return quote;
      setRequoting(true);
      try {
        return await fetchQuote({ requestedMedId: nextReq, fulfillmentRoute: nextRoute });
      } finally {
        setRequoting(false);
      }
    },
    [status, quote, fetchQuote],
  );

  // Expired before checkout → refresh silently (same params).
  const refreshIfExpired = useCallback(() => {
    if (quote && quoteExpired(quote)) return requote({ force: true });
    return Promise.resolve(quote);
  }, [quote, requote]);

  // Price a pick WITHOUT changing the active quote (the "you asked for X last
  // time" callout shows the price before the patient commits). Read-only.
  const previewQuote = useCallback(
    async (requestedMedId) => {
      if (!masterId || !requestedMedId) return null;
      const params = { masterId, requestedMedId };
      if (rxId) params.rxId = rxId;
      else if (rxRowId) params.rxRowId = rxRowId;
      else if (medId) params.medId = medId;
      if (lastExtraRef.current.fulfillmentRoute) params.fulfillmentRoute = lastExtraRef.current.fulfillmentRoute;
      try {
        const q = await apiService.getFollowupQuote(params);
        return q && q.quoteId ? q : null;
      } catch (_) {
        return null;
      }
    },
    [masterId, rxId, rxRowId, medId],
  );

  const reset = useCallback(() => {
    seq.current += 1;
    rxMedIdRef.current = null;
    setQuote(null);
    setStatus('idle');
    setRequoting(false);
    lastExtraRef.current = { requestedMedId: null, fulfillmentRoute: fulfillmentRoute || null };
  }, [fulfillmentRoute]);

  return { quote, status, requoting, requote, previewQuote, refreshIfExpired, reset };
}
